import type { PendingServicePointState } from '@rheora/enrollment-contract';

/**
 * E-SF20 — what the pending-service page says for a given state. PURE.
 *
 * No React, no `t()` — it returns i18n KEYS and the component resolves them, the
 * same contract `meterDetail.service.ts` uses.
 *
 * ── THE LIFECYCLE HAS SEVEN STATES; THE CUSTOMER SEES FOUR STORIES ─────────
 * `DECLARED` and `AWAITING_ESIID` are a real distinction to us — one means the
 * record exists, the other that a detection loop is watching it — and NO
 * distinction at all to the person reading the page. Both mean "your meter isn't
 * there yet". Rendering them differently would expose our internal plumbing as if
 * it were news about their house, and would make the page appear to change when
 * nothing about the build had.
 *
 * So the mapping collapses deliberately, and the four groups are the four things
 * a customer can actually DO: keep waiting, keep waiting (but it's moving), sign
 * up now, or stop.
 */

/** Which visual treatment the page uses. Not a colour — the component picks that. */
export type PendingServiceTone = 'waiting' | 'progress' | 'ready' | 'closed';

export interface PendingServiceCopy {
  readonly headingKey: string;
  readonly bodyKey: string;
  readonly tone: PendingServiceTone;
  /** Whether the page offers a route back into the funnel. */
  readonly showsShopCta: boolean;
}

export function pendingServiceCopy(state: PendingServicePointState): PendingServiceCopy {
  switch (state) {
    case 'DECLARED':
    case 'AWAITING_ESIID':
      return {
        headingKey: 'shop.pendingService.state.waiting.heading',
        bodyKey: 'shop.pendingService.state.waiting.body',
        tone: 'waiting',
        showsShopCta: false,
      };

    case 'ESIID_PUBLISHED':
    case 'ENERGIZED_PENDING_FIRST_READ':
      return {
        headingKey: 'shop.pendingService.state.progress.heading',
        bodyKey: 'shop.pendingService.state.progress.body',
        tone: 'progress',
        showsShopCta: false,
      };

    case 'LIVE':
      return {
        headingKey: 'shop.pendingService.state.live.heading',
        bodyKey: 'shop.pendingService.state.live.body',
        tone: 'ready',
        // The only state where there is something to do, so the only one that
        // sends them back to the funnel.
        showsShopCta: true,
      };

    case 'SUPERSEDED':
      return {
        headingKey: 'shop.pendingService.state.superseded.heading',
        bodyKey: 'shop.pendingService.state.superseded.body',
        tone: 'closed',
        // This address already had service, so there was never anything to wait
        // for — shopping is exactly the right next step.
        showsShopCta: true,
      };

    case 'ABANDONED':
      return {
        headingKey: 'shop.pendingService.state.abandoned.heading',
        bodyKey: 'shop.pendingService.state.abandoned.body',
        tone: 'closed',
        showsShopCta: true,
      };
  }
}

/**
 * Whether the overdue notice should be shown.
 *
 * ⚠️ NOT simply `overdue`. The server computes the flag from stored dates without
 * regard to state, so a record that has already reached the meter — or been closed
 * — can still carry it. Telling someone whose meter is installed that their build
 * is running late is worse than saying nothing: it is stale news presented as
 * current, on the one page they trust for the current answer.
 */
export function showsOverdueNotice(state: PendingServicePointState, overdue: boolean): boolean {
  if (!overdue) return false;
  return state === 'DECLARED' || state === 'AWAITING_ESIID';
}
